import { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { EditorContext } from '@wix/da-editor-tiptap/pkg/components/Editor/Context';
import { usePreviousValue } from '@wix/da-hooks/pkg/usePreviousValue';
import { resetEditorPublishResults } from '../redux/actionCreators';
import { CommentPublisherResult } from '../redux/types';

/**
 * Runs a couple cleanup actions whenever the editor's publish result comes back.
 *
 * Relies on all the contexts provided by `<CommentEditorProviders />`,
 * so be sure you have it set up first!
 */
export function usePublishSuccessCleanup(
  editorId: string,
  result: CommentPublisherResult | null,
  cleanupCallback?: (result: CommentPublisherResult) => void
) {
  const { ref: editor } = useContext(EditorContext);
  const dispatch = useDispatch();
  const previousResult = usePreviousValue(result);

  /*
    Call our cleanup callback whenever the result changes.

    We might be able to include this in the useEffect below
    but it was separate in the class component and checks the
    previous result.
  */
  useEffect(() => {
    if (result && previousResult !== result) {
      cleanupCallback && cleanupCallback(result);
    }
  }, [result, previousResult, cleanupCallback]);

  /*
    Blur the editor and reset the editor's publish result when
    a result comes back
  */
  useEffect(() => {
    if (result) {
      dispatch(resetEditorPublishResults(editorId));

      editor?.commands.clearContent(true);
      // Timeout because of weird timing around the editor closing
      // TODO: Remove the need for this timeout
      const timeout = setTimeout(() => editor?.commands.blur(), 100);
      return () => clearTimeout(timeout);
    }
  }, [result, editor, dispatch, editorId]);
}
